import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Trash2 } from 'lucide-react';
import Title from '@/components/ui/Title';
import OfferFormContent from './OfferFormContent';
import PopUpsSection, { type PopUpsSectionRef } from './PopUpsSection';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { RootState } from '@/app/store';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { fetchOfferPurchaseNotificationConfig } from '@/features/live/liveThunks';
import type { WebinarPurchaseNotificationConfigInput } from '@/features/live/types';
import {
  buildDisabledPurchaseNotificationInput,
  isNotificationConfigReady,
  resolveNotificationSwitchState,
} from '@/lib/purchaseNotificationForm';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import Loader from '@/components/ui/Loader';
import { InputLabel } from '@/components/ui/InputLabel';
import { TextFieldWithLimit } from '@/components/ui/TextFieldWithLimit';
import type { OfferTemplate, OfferTemplateContent } from '@/features/webinar/types';
import { getOfferEmbedCodeValue, getStripePriceOptionInputs } from '@/lib/offerEmbed';
import { makeOfferSchema, type OfferFormValues } from './offerFormSchema';
import { getReadableTextColor } from '@/utils/colors';

interface OfferFormProps {
  offerIndex: number;
  accordionValue: string;
  onRemove: () => void;
  onSubmit: (
    data: OfferFormValues,
    purchaseNotificationInput?: WebinarPurchaseNotificationConfigInput,
  ) => Promise<number | null>;
  canRemove: boolean;
  existingOffer?: OfferTemplate;
  isModal?: boolean;
  onSave?: () => void;
  onDelete?: () => void;
  webinarId?: number;
  workspaceId?: number;
  templateId?: number;
  isSubmitting?: boolean;
  isEvergreen?: boolean;
  readOnly?: boolean;
}

export type { OfferFormValues } from './offerFormSchema';

// Une ligne vide par défaut : le formulaire affiche directement le choix du premier prix.
function toStripePriceRows(content: OfferTemplateContent | undefined) {
  const options = getStripePriceOptionInputs(content);
  return options.length ? options : [{ priceId: '', installmentCount: null }];
}

export default function OfferForm({
  offerIndex,
  accordionValue,
  canRemove,
  onRemove,
  onSubmit,
  existingOffer,
  isModal,
  onSave,
  onDelete,
  isSubmitting = false,
  isEvergreen,
  readOnly,
  webinarId,
  workspaceId,
  templateId,
}: OfferFormProps) {
  const locked = readOnly === true;
  const { t } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();
  const visualIdentity = useAppSelector(
    (state: RootState) => state.workspace.currentWorkspace?.visualIdentity,
  );

  const purchaseNotificationRef = useRef<PopUpsSectionRef>(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(() =>
    resolveNotificationSwitchState(templateId, undefined),
  );
  const [isSavingAll, setIsSavingAll] = useState(false);
  const purchaseNotificationConfig = useAppSelector((state: RootState) =>
    templateId != null ? state.live.offerPurchaseNotificationConfigs[templateId] : undefined,
  );
  const purchaseNotificationConfigLoading = useAppSelector((state: RootState) =>
    templateId != null ? state.live.offerPurchaseNotificationConfigLoading[templateId] : undefined,
  );
  const notificationConfigReady = isNotificationConfigReady(
    templateId,
    purchaseNotificationConfigLoading,
  );

  useEffect(() => {
    if (!workspaceId || !webinarId || templateId == null) return;
    dispatch(fetchOfferPurchaseNotificationConfig({ workspaceId, webinarId, offerId: templateId }));
  }, [dispatch, workspaceId, webinarId, templateId]);

  const hasToggledNotificationsRef = useRef(false);

  useEffect(() => {
    if (hasToggledNotificationsRef.current) return;
    if (purchaseNotificationConfig) {
      setNotificationsEnabled(
        resolveNotificationSwitchState(templateId, purchaseNotificationConfig),
      );
    }
  }, [purchaseNotificationConfig, templateId]);

  const handleNotificationsToggle = (checked: boolean) => {
    hasToggledNotificationsRef.current = true;
    setNotificationsEnabled(checked);
  };
  const isCustomColor = (color: string | undefined): boolean => {
    if (!color) return false;
    return /^#[0-9A-Fa-f]{6}$/.test(color);
  };

  const mapColorToId = (
    colorValue: string | null | undefined,
    defaultColorId: string = 'background',
  ): string => {
    if (!colorValue) {
      return defaultColorId;
    }

    if (colorValue === 'gradient1') {
      return 'gradient1-color1';
    }
    if (colorValue === 'gradient2') {
      return 'gradient2-color1';
    }

    if (!isCustomColor(colorValue)) {
      return colorValue;
    }

    const normalizedColor = colorValue.toUpperCase();
    if (visualIdentity) {
      if (
        visualIdentity.accentColor &&
        normalizedColor === visualIdentity.accentColor.toUpperCase()
      ) {
        return 'accent';
      }
      if (
        visualIdentity.backgroundColor &&
        normalizedColor === visualIdentity.backgroundColor.toUpperCase()
      ) {
        return 'background';
      }
      if (
        visualIdentity.contrastColor &&
        normalizedColor === visualIdentity.contrastColor.toUpperCase()
      ) {
        return 'contrast';
      }
      if (
        visualIdentity.secondaryBackgroundColor &&
        normalizedColor === visualIdentity.secondaryBackgroundColor.toUpperCase()
      ) {
        return 'secondary-background';
      }
      if (
        visualIdentity.gradient1Color1 &&
        normalizedColor === visualIdentity.gradient1Color1.toUpperCase()
      ) {
        return 'gradient1-color1';
      }
      if (
        visualIdentity.gradient2Color1 &&
        normalizedColor === visualIdentity.gradient2Color1.toUpperCase()
      ) {
        return 'gradient2-color1';
      }
    }

    if (normalizedColor === '#000000') {
      return 'black';
    }
    if (normalizedColor === '#FFFFFF') {
      return 'white';
    }

    return 'custom';
  };

  const getDefaultValues = (): OfferFormValues => {
    // Parité avec quiz/sondage : fond de carte = accent (il ressort du fond), bouton = contraste
    // (il ressort de la carte). Les couleurs de texte par défaut sont calculées pour rester
    // lisibles sur leur fond respectif : titre et texte sur la carte (accent), libellé sur le bouton (contraste).
    // Le client peut toujours forcer une couleur via les sélecteurs.
    const defaultCardColor = visualIdentity?.accentColor ?? '#2effb8';
    const defaultCardTextColor = getReadableTextColor(defaultCardColor);
    const defaultButtonColor = visualIdentity?.contrastColor ?? '#2c6cff';
    const defaultButtonTextColor = getReadableTextColor(defaultButtonColor);
    return {
      privateTitle: existingOffer?.privateTitle || '',
      abTesting: existingOffer?.abTesting ?? false,
      splitPercentA: existingOffer?.splitPercentA ?? 50,
      bPercent: 100 - (existingOffer?.splitPercentA ?? 50),

      titleA: existingOffer?.contents?.[0]?.title ?? '',
      subTitleA: existingOffer?.contents?.[0]?.subTitle ?? '',
      descriptionA: existingOffer?.contents?.[0]?.description ?? '',
      buttonLabelA: existingOffer?.contents?.[0]?.buttonLabel ?? '',
      conversionModeA: existingOffer?.contents?.[0]?.conversionMode ?? 'EXTERNAL_LINK',
      buttonLinkA: existingOffer?.contents?.[0]?.buttonLink ?? '',
      embedCodeA: getOfferEmbedCodeValue(existingOffer?.contents?.[0]),
      stripePricesA: toStripePriceRows(existingOffer?.contents?.[0]),
      backgroundColorA: mapColorToId(
        existingOffer?.contents?.[0]?.backgroundColor ?? visualIdentity?.accentColor,
        'accent',
      ),
      borderColorA: existingOffer?.contents?.[0]?.borderColor
        ? (mapColorToId(existingOffer?.contents?.[0]?.borderColor) ??
          mapColorToId(visualIdentity?.accentColor, 'accent'))
        : 'no-border',
      // existingOffer?.contents?.[0]?.borderColor ||
      // (mapColorToId(existingOffer?.contents?.[0]?.borderColor)
      //   ? 'custom'
      //   : existingOffer?.contents?.[0]?.borderColor) ||
      // 'accent',
      titleColorA: existingOffer?.contents?.[0]?.titleColor ?? defaultCardTextColor,

      textColorA: existingOffer?.contents?.[0]?.textColor ?? defaultCardTextColor,
      buttonColorA: mapColorToId(
        existingOffer?.contents?.[0]?.buttonColor ?? visualIdentity?.contrastColor,
        'contrast',
      ),
      buttonTextColorA: existingOffer?.contents?.[0]?.buttonTextColor ?? defaultButtonTextColor,
      iconA: existingOffer?.contents?.[0]?.icon ?? 'percent',
      iconColorA: '#000000',
      customIconColorA: '#000000',
      iconBackgroundColorA: '#000000',
      customIconBackgroundColorA: '#000000',

      showImageA: existingOffer?.contents?.[0]?.imageUrl ? true : false,
      imgA: existingOffer?.contents?.[0]?.imageUrl ?? undefined,
      showIconA: existingOffer?.contents?.[0]?.icon ? true : false,
      customBackgroundColorA: isCustomColor(existingOffer?.contents?.[0]?.backgroundColor)
        ? existingOffer?.contents?.[0]?.backgroundColor
        : '#000000',
      customBorderColorA: isCustomColor(existingOffer?.contents?.[0]?.borderColor)
        ? existingOffer?.contents?.[0]?.borderColor
        : '#000000',
      customButtonColorA: isCustomColor(existingOffer?.contents?.[0]?.buttonColor)
        ? existingOffer?.contents?.[0]?.buttonColor
        : '#000000',

      titleB: existingOffer?.contents?.[1]?.title ?? '',
      subTitleB: existingOffer?.contents?.[1]?.subTitle ?? null,
      descriptionB: existingOffer?.contents?.[1]?.description ?? null,
      buttonLabelB: existingOffer?.contents?.[1]?.buttonLabel ?? '',
      conversionModeB: existingOffer?.contents?.[1]?.conversionMode ?? 'EXTERNAL_LINK',
      buttonLinkB: existingOffer?.contents?.[1]?.buttonLink ?? '',
      embedCodeB: getOfferEmbedCodeValue(existingOffer?.contents?.[1]),
      stripePricesB: toStripePriceRows(existingOffer?.contents?.[1]),
      backgroundColorB: mapColorToId(
        existingOffer?.contents?.[1]?.backgroundColor ?? visualIdentity?.accentColor,
        'accent',
      ),
      borderColorB:
        mapColorToId(existingOffer?.contents?.[1]?.borderColor) ??
        mapColorToId(visualIdentity?.accentColor, 'accent'),
      titleColorB: existingOffer?.contents?.[1]?.titleColor ?? defaultCardTextColor,
      textColorB: existingOffer?.contents?.[1]?.textColor ?? defaultCardTextColor,

      buttonColorB: mapColorToId(
        existingOffer?.contents?.[1]?.buttonColor ?? visualIdentity?.contrastColor,
        'contrast',
      ),
      buttonTextColorB: existingOffer?.contents?.[1]?.buttonTextColor ?? defaultButtonTextColor,
      iconB: existingOffer?.contents?.[1]?.icon ?? 'percent',
      iconColorB: mapColorToId(
        existingOffer?.contents?.[1]?.iconColor ?? visualIdentity?.accentColor,
        'accent',
      ),
      customIconColorB: '#000000',
      iconBackgroundColorB: '#000000',
      customIconBackgroundColorB: '#000000',

      showImageB: existingOffer?.contents?.[1]?.imageUrl ? true : false,
      imgB: existingOffer?.contents?.[1]?.imageUrl ?? undefined,
      showIconB: existingOffer?.contents?.[1]?.icon ? true : false,
      customBackgroundColorB: isCustomColor(existingOffer?.contents?.[1]?.backgroundColor)
        ? existingOffer?.contents?.[1]?.backgroundColor
        : '#000000',
      customBorderColorB: isCustomColor(existingOffer?.contents?.[1]?.borderColor)
        ? existingOffer?.contents?.[1]?.borderColor
        : '#000000',
      customButtonColorB: isCustomColor(
        existingOffer?.contents?.[1]?.buttonColor ?? existingOffer?.contents?.[1]?.buttonColor,
      )
        ? (existingOffer?.contents?.[1]?.buttonColor ?? existingOffer?.contents?.[1]?.buttonColor)
        : '#000000',
    };
  };

  const offerSchema = useMemo(() => makeOfferSchema(t), [t]);
  const form = useForm<OfferFormValues>({
    resolver: zodResolver(offerSchema),
    defaultValues: getDefaultValues(),
    mode: 'onChange',
  });

  const abTesting = useWatch({
    control: form.control,
    name: 'abTesting',
  });

  const previousOfferIdRef = useRef<number | undefined>(existingOffer?.id);
  const previousOfferContentsRef = useRef<string | undefined>(
    existingOffer?.contents ? JSON.stringify(existingOffer.contents) : undefined,
  );

  const handleSubmit = (data: OfferFormValues) => {
    onSubmit(data);
  };

  const handleSaveOfferAndNotification = async () => {
    if (locked || isSavingAll) return;
    // Sauvegarder avant la réponse enverrait une config vide qui écraserait la notification existante.
    if (!notificationConfigReady) return;

    const isOfferValid = await form.trigger();
    if (!isOfferValid) return;

    let notificationInput = buildDisabledPurchaseNotificationInput(purchaseNotificationConfig);
    if (notificationsEnabled) {
      const filledInput = await purchaseNotificationRef.current?.buildPurchaseNotificationInput();
      if (!filledInput) return;
      notificationInput = filledInput;
    }

    setIsSavingAll(true);
    try {
      await onSubmit(form.getValues(), notificationInput);
    } finally {
      setIsSavingAll(false);
    }
  };

  const purchaseNotificationModalBlock = (
    <>
      <Separator className="bg-input my-4" />
      {templateId ? (
        <PopUpsSection
          visualIdentity={visualIdentity}
          webinarId={webinarId}
          workspaceId={workspaceId}
          offerId={templateId}
          readOnly={readOnly}
        />
      ) : (
        <p className="text-sm text-tertiary">{t('offersForm.notifications.saveOfferFirst')}</p>
      )}
    </>
  );

  const purchaseNotificationBlock = (
    <>
      <Separator className="bg-input my-4" />

      <div className="flex items-center justify-between gap-2">
        <Label>{t('offersForm.notifications.addNotificationLabel')}</Label>
        {notificationConfigReady ? (
          <Switch
            checked={notificationsEnabled}
            onCheckedChange={handleNotificationsToggle}
            disabled={locked}
          />
        ) : (
          <Loader />
        )}
      </div>

      {notificationConfigReady && notificationsEnabled && (
        <PopUpsSection
          ref={purchaseNotificationRef}
          visualIdentity={visualIdentity}
          webinarId={webinarId}
          workspaceId={workspaceId}
          offerId={templateId}
          readOnly={readOnly}
          hideSaveButton
        />
      )}

      {!locked && (
        <Button
          type="button"
          onClick={handleSaveOfferAndNotification}
          className="mt-4 w-full disabled:bg-gray-200 disabled:text-gray-500"
          disabled={
            isSubmitting || isSavingAll || !notificationConfigReady || !form.formState.isValid
          }
        >
          {(isSubmitting || isSavingAll) && <Loader />}
          {t('save')}
        </Button>
      )}
    </>
  );

  useEffect(() => {
    const currentOfferId = existingOffer?.id;
    const currentOfferContents = existingOffer?.contents
      ? JSON.stringify(existingOffer.contents)
      : undefined;

    if (
      currentOfferId !== previousOfferIdRef.current ||
      currentOfferContents !== previousOfferContentsRef.current
    ) {
      previousOfferIdRef.current = currentOfferId;
      previousOfferContentsRef.current = currentOfferContents;
      if (existingOffer) {
        const newDefaultValues = getDefaultValues();
        form.reset(newDefaultValues);
      }
    }
  }, [existingOffer?.id, existingOffer?.contents]);

  useEffect(() => {
    if (onSave) {
      const saveFunction = () => {
        form.handleSubmit(handleSubmit)();
      };

      (window as any).offerFormSave = saveFunction;
    }

    if (onDelete) {
      (window as any).offerFormDelete = onDelete;
    }

    return () => {
      delete (window as any).offerFormSave;
      delete (window as any).offerFormDelete;
    };
  }, [onSave, onDelete, form]);

  useEffect(() => {
    if (abTesting) {
      const titleB = form.getValues('titleB');
      if (!titleB || titleB.trim() === '') {
        const valuesA = form.getValues();
        form.setValue('titleB', valuesA.titleA || '');
        form.setValue('subTitleB', valuesA.subTitleA);
        form.setValue('descriptionB', valuesA.descriptionA);
        form.setValue('buttonLabelB', valuesA.buttonLabelA || '');
        form.setValue('conversionModeB', valuesA.conversionModeA);
        form.setValue('buttonLinkB', valuesA.buttonLinkA || '');
        form.setValue('embedCodeB', valuesA.embedCodeA || '');
        form.setValue(
          'stripePricesB',
          valuesA.stripePricesA.map((option) => ({ ...option })),
        );
        form.setValue('backgroundColorB', valuesA.backgroundColorA);
        form.setValue('borderColorB', valuesA.borderColorA);
        form.setValue('titleColorB', valuesA.titleColorA);
        form.setValue('textColorB', valuesA.textColorA);
        form.setValue('buttonColorB', valuesA.buttonColorA);
        form.setValue('buttonTextColorB', valuesA.buttonTextColorA);
        form.setValue('iconB', valuesA.iconA);
        form.setValue('iconColorB', valuesA.iconColorA);
        form.setValue('customIconColorB', '#000000');
        form.setValue('iconBackgroundColorB', '#000000');
        form.setValue('customIconBackgroundColorB', '#000000');
        form.setValue('showImageB', valuesA.showImageA);
        form.setValue('imgB', valuesA.imgA);
        form.setValue('showIconB', valuesA.showIconA);
        form.setValue('customBackgroundColorB', valuesA.customBackgroundColorA);
        form.setValue('customBorderColorB', valuesA.customBorderColorA);
        form.setValue('customButtonColorB', valuesA.customButtonColorA);
      }
    }
  }, [abTesting, form]);

  return (
    <Form {...form}>
      <div>
        {isModal ? (
          <div>
            <div className="flex flex-col  w-full">
              <div className="flex items-start gap-2 w-full">
                <div className="flex-1">
                  <FormField
                    name="privateTitle"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <TextFieldWithLimit
                          label={t('offersForm.privateTitleLabel')}
                          value={field.value}
                          onChange={(v) => field.onChange(v)}
                          disabled={locked}
                          error={fieldState.error?.message}
                        />
                      </FormItem>
                    )}
                  />
                </div>
                {canRemove && !locked && (
                  <button
                    aria-label={t('offersForm.deleteOffer')}
                    title={t('offersForm.deleteOffer')}
                    type="button"
                    onClick={onRemove}
                    className="bg-white rounded-lg h-14 mr-4 py-3 px-2 border border-input cursor-pointer"
                  >
                    <Trash2 className="size-6" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-4  bg-medium pt-4 px-0">
              <Title level={2}>{t('offersForm.offerTitle')}</Title>

              <div className="flex items-center justify-between gap-2">
                <Label>{t('offersForm.abTestingLabel')}</Label>
                <FormField
                  name="abTesting"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <Switch
                        checked={field.value ?? false}
                        onCheckedChange={field.onChange}
                        disabled={locked}
                      />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                name="abTesting"
                control={form.control}
                render={() => (
                  <FormItem>
                    <FormMessage className="text-left text-red-500 text-sm" />
                  </FormItem>
                )}
              />

              {form.watch('abTesting') ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="splitPercentA"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <InputLabel
                              label={t('offersForm.splitPercent.variantA')}
                              type="number"
                              min={0}
                              max={100}
                              value={field.value?.toString() || ''}
                              onChange={(e) => {
                                const value = parseInt(e.target.value) || 0;
                                field.onChange(value);
                                const bValue = 100 - value;
                                form.setValue('bPercent', bValue);
                              }}
                              disabled={locked}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="bPercent"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <InputLabel
                              label={t('offersForm.splitPercent.variantB')}
                              type="number"
                              min={0}
                              max={100}
                              value={field.value?.toString() || ''}
                              onChange={(e) => {
                                const value = parseInt(e.target.value) || 0;
                                field.onChange(value);
                                const aValue = 100 - value;
                                form.setValue('splitPercentA', aValue);
                              }}
                              disabled={locked}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Tabs defaultValue="offer-a">
                    <TabsList className="w-full mb-4">
                      <TabsTrigger value="offer-a">{t('offersForm.tabs.offerA')}</TabsTrigger>
                      <TabsTrigger value="offer-b">{t('offersForm.tabs.offerB')}</TabsTrigger>
                    </TabsList>

                    <TabsContent value="offer-a">
                      <OfferFormContent
                        visualIdentity={visualIdentity}
                        form={form}
                        offerType="A"
                        offerIndex={offerIndex}
                        readOnly={readOnly}
                        workspaceId={workspaceId}
                        webinarId={webinarId}
                        templateId={templateId}
                        stripePaymentAvailability={
                          existingOffer?.contents?.[0]?.stripePaymentAvailability
                        }
                        stripeCheckoutAllowed={!isEvergreen}
                      />
                    </TabsContent>
                    <TabsContent value="offer-b">
                      <OfferFormContent
                        visualIdentity={visualIdentity}
                        form={form}
                        offerType="B"
                        offerIndex={offerIndex}
                        readOnly={readOnly}
                        workspaceId={workspaceId}
                        webinarId={webinarId}
                        templateId={templateId}
                        stripePaymentAvailability={
                          existingOffer?.contents?.[1]?.stripePaymentAvailability
                        }
                        stripeCheckoutAllowed={!isEvergreen}
                      />
                    </TabsContent>
                  </Tabs>
                </>
              ) : (
                <OfferFormContent
                  visualIdentity={visualIdentity}
                  form={form}
                  offerType="A"
                  offerIndex={offerIndex}
                  readOnly={readOnly}
                  workspaceId={workspaceId}
                  webinarId={webinarId}
                  templateId={templateId}
                  stripePaymentAvailability={
                    existingOffer?.contents?.[0]?.stripePaymentAvailability
                  }
                  stripeCheckoutAllowed={!isEvergreen}
                />
              )}
              {purchaseNotificationModalBlock}
            </div>
          </div>
        ) : (
          <AccordionItem
            key={accordionValue}
            value={accordionValue}
            className={`bg-medium p-6 ${isEvergreen ? 'rounded-none rounded-t-[20px] border-b-2' : ''}`}
          >
            <div className="flex flex-col  w-full">
              <div className="flex items-start gap-2 w-full">
                <div className="flex-1">
                  <FormField
                    name="privateTitle"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <TextFieldWithLimit
                          className="bg-white"
                          label={t('offersForm.privateTitleLabel')}
                          value={field.value}
                          onChange={(v) => field.onChange(v)}
                          disabled={locked}
                          error={fieldState.error?.message}
                        />
                      </FormItem>
                    )}
                  />
                </div>
                {canRemove && !locked && (
                  <button
                    aria-label={t('offersForm.deleteOffer')}
                    title={t('offersForm.deleteOffer')}
                    type="button"
                    onClick={onRemove}
                    className="bg-white rounded-lg h-14 mr-4 py-3 px-2 border border-input cursor-pointer"
                  >
                    <Trash2 className="size-6" />
                  </button>
                )}
                <AccordionTrigger isChevron className=" ] p-0" />
              </div>
            </div>
            <AccordionContent className="flex flex-col gap-4  p-0 pt-4">
              <Title level={2}>{t('offersForm.offerTitle')}</Title>

              <div className="flex items-center justify-between gap-2">
                <Label>{t('offersForm.abTestingLabel')}</Label>
                <FormField
                  name="abTesting"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <Switch
                        checked={field.value ?? false}
                        onCheckedChange={field.onChange}
                        disabled={locked}
                      />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                name="abTesting"
                control={form.control}
                render={() => (
                  <FormItem>
                    <FormMessage className="text-left text-red-500 text-sm" />
                  </FormItem>
                )}
              />

              {form.watch('abTesting') ? (
                <>
                  <div className="grid lg:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="splitPercentA"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <InputLabel
                              className="bg-white"
                              label={t('offersForm.splitPercent.variantA')}
                              type="number"
                              min={0}
                              max={100}
                              value={field.value?.toString() || ''}
                              onChange={(e) => {
                                const value = parseInt(e.target.value) || 0;
                                field.onChange(value);
                                const bValue = 100 - value;
                                form.setValue('bPercent', bValue);
                              }}
                              disabled={locked}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="bPercent"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <InputLabel
                              label={t('offersForm.splitPercent.variantB')}
                              type="number"
                              min={0}
                              max={100}
                              value={field.value?.toString() || ''}
                              className="bg-white"
                              onChange={(e) => {
                                const value = parseInt(e.target.value) || 0;
                                field.onChange(value);
                                const aValue = 100 - value;
                                form.setValue('splitPercentA', aValue);
                              }}
                              disabled={locked}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Tabs defaultValue="offer-a">
                    <TabsList className="w-full mb-4">
                      <TabsTrigger value="offer-a">{t('offersForm.tabs.offerA')}</TabsTrigger>
                      <TabsTrigger value="offer-b">{t('offersForm.tabs.offerB')}</TabsTrigger>
                    </TabsList>

                    <TabsContent value="offer-a">
                      <OfferFormContent
                        visualIdentity={visualIdentity}
                        form={form}
                        offerType="A"
                        offerIndex={offerIndex}
                        readOnly={readOnly}
                        workspaceId={workspaceId}
                        webinarId={webinarId}
                        templateId={templateId}
                        stripePaymentAvailability={
                          existingOffer?.contents?.[0]?.stripePaymentAvailability
                        }
                        stripeCheckoutAllowed={!isEvergreen}
                      />
                    </TabsContent>
                    <TabsContent value="offer-b">
                      <OfferFormContent
                        visualIdentity={visualIdentity}
                        form={form}
                        offerType="B"
                        offerIndex={offerIndex}
                        readOnly={readOnly}
                        workspaceId={workspaceId}
                        webinarId={webinarId}
                        templateId={templateId}
                        stripePaymentAvailability={
                          existingOffer?.contents?.[1]?.stripePaymentAvailability
                        }
                        stripeCheckoutAllowed={!isEvergreen}
                      />
                    </TabsContent>
                  </Tabs>
                </>
              ) : (
                <OfferFormContent
                  visualIdentity={visualIdentity}
                  form={form}
                  offerType="A"
                  offerIndex={offerIndex}
                  readOnly={readOnly}
                  workspaceId={workspaceId}
                  webinarId={webinarId}
                  templateId={templateId}
                  stripePaymentAvailability={
                    existingOffer?.contents?.[0]?.stripePaymentAvailability
                  }
                  stripeCheckoutAllowed={!isEvergreen}
                />
              )}
              {purchaseNotificationBlock}
            </AccordionContent>
          </AccordionItem>
        )}
      </div>
    </Form>
  );
}
